import { Injectable, inject } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { AuthService } from "./auth.service";
import { FunctionsService } from "./functions.service";
import { BvToastService } from "../design-system";

@Injectable({
  providedIn: "root",
})
export class ApiService {
  // Mirrors the old `firebase.functions()` surface (.httpsCallable(name)), so
  // every call site below is unchanged; FunctionsService wraps modular callables.
  private fbFunc = inject(FunctionsService);
  private toast = inject(BvToastService);
  private translate = inject(TranslateService);
  constructor(private auth: AuthService) {}

  // Catch handler for callables whose call sites don't handle rejections
  // themselves. Resolves to undefined (same contract the old swallowing
  // catches gave every call site) but surfaces the failure to the user.
  private surfaceError = (error: any): void => {
    console.error(error.code, error.message);
    this.toast.open(
      this.translate.instant("GENERIC_ERROR"),
      this.translate.instant("OK"),
      { duration: 5000 }
    );
  };
  tournamentRegister(tournID: string, uid: string, extra?: any) {
    let f = this.fbFunc.httpsCallable("tournamentRegister");
    return f({ tournID, uid, ...(extra || {}) }).catch(this.surfaceError);
  }
  tournamentUnregister(tournID: string, uid: string, undoAction?: boolean) {
    let f = this.fbFunc.httpsCallable("tournamentUnregister");
    return f({ tournID, uid, undoAction: !!undoAction }).catch(this.surfaceError);
  }
  movePlayer(
    tournID: string,
    playerID: string,
    manualTable?: number,
    manualSeat?: number,
    skipLog?: boolean
  ) {
    let f = this.fbFunc.httpsCallable("movePlayer");
    const payload: any = { tournID, playerID };
    if (manualTable && manualSeat) {
      payload.manualTable = manualTable;
      payload.manualSeat = manualSeat;
    }
    if (skipLog) payload.skipLog = true;
    return f(payload).catch(this.surfaceError);
  }
  drawSeats(tournID: string) {
    let f = this.fbFunc.httpsCallable("drawSeats");
    return f({ tournID }).catch(this.surfaceError);
  }
  advanceShootoutRound(tournID: string) {
    let f = this.fbFunc.httpsCallable("advanceShootoutRound");
    return f({ tournID }).catch(this.surfaceError);
  }
  breakTable(tournID: string, tableNumber?: number) {
    let f = this.fbFunc.httpsCallable("breakTable");
    return f({ tournID, tableNumber }).catch(this.surfaceError);
  }
  balanceTables(tournID: string) {
    let f = this.fbFunc.httpsCallable("balanceTables");
    return f({ tournID }).catch(this.surfaceError);
  }
  addTable(tournID: string) {
    let f = this.fbFunc.httpsCallable("addTable");
    return f({ tournID }).catch(this.surfaceError);
  }
  updateSeatingParams(
    tournID: string,
    params: {
      extraSeats: number;
      perTable: number;
      numberPlayersFinalTable: number;
    }
  ) {
    let f = this.fbFunc.httpsCallable("updateSeatingParams");
    return f({ tournID, ...params }).catch(this.surfaceError);
  }
  tournamentRebuy(tournID: string, playerID?: string) {
    let f = this.fbFunc.httpsCallable("tournamentRebuy");
    return f({ tournID, playerID });
  }
  tournamentAddon(tournID: string, playerID?: string) {
    let f = this.fbFunc.httpsCallable("tournamentAddon");
    return f({ tournID, playerID });
  }
  tournamentReentry(tournID: string) {
    let f = this.fbFunc.httpsCallable("tournamentReentry");
    return f({ tournID });
  }
  countElimination(tournID: string) {
    let f = this.fbFunc.httpsCallable("countElimination");
    return f({ tournID });
  }
  adjustActPlayers(tournID: string, delta: number) {
    let f = this.fbFunc.httpsCallable("adjustActPlayers");
    return f({ tournID, delta });
  }
  tournamentBonusChips(tournID: string, playerID: string, bonusChips: number) {
    let f = this.fbFunc.httpsCallable("tournamentBonusChips");
    return f({ tournID, playerID, bonusChips });
  }
  tournamentSetBounty(tournID: string, playerID: string, bountyValue: number) {
    let f = this.fbFunc.httpsCallable("tournamentSetBounty");
    return f({ tournID, playerID, bountyValue });
  }
  tournamentClearBounties(tournID: string) {
    let f = this.fbFunc.httpsCallable("tournamentClearBounties");
    return f({ tournID });
  }
  logUndo(tournID: string, logKey: string) {
    let f = this.fbFunc.httpsCallable("logUndo");
    return f({ tournID, logKey });
  }
  // Trade two players' finishing positions (or bring a wrongly eliminated
  // player back in for an active one). Server-authoritative, logged + undoable.
  swapFinish(tournID: string, uidA: string, uidB: string) {
    let f = this.fbFunc.httpsCallable("swapFinish");
    return f({ tournID, uidA, uidB });
  }
  profUndoElimination(uid: string, tournID: string) {
    let f = this.fbFunc.httpsCallable("profUndoElimination");
    f({
      uid: uid,
      tournID: tournID,
    }).catch(this.surfaceError);
  }
  profSetFinish(uid: string, tournID: string, finish: number) {
    let f = this.fbFunc.httpsCallable("profSetFinish");
    f({
      uid: uid,
      tournID: tournID,
      finish: finish,
    }).catch(this.surfaceError);
  }
  deleteTournament(tournID: string, cascade?: boolean) {
    let f = this.fbFunc.httpsCallable("deleteTournament");
    // No surfaceError catch: club.service handles rejections itself — the
    // structured SPLIT_PARENT_HAS_CHILDREN error drives the cascade confirm.
    return f({
      tournID: tournID,
      ...(cascade ? { cascade: true } : {}),
    });
  }
  markIncomplete(tournID: string) {
    let f = this.fbFunc.httpsCallable("markIncomplete");
    return f({
      tournID: tournID,
    }).catch(this.surfaceError);
  }
  updateTournamentSettings(settings: any) {
    let f = this.fbFunc.httpsCallable("updateTournamentSettings");
    return f(settings).catch(this.surfaceError);
  }
  updateClub(params: any) {
    let f = this.fbFunc.httpsCallable("updateClub");
    f(params).catch(this.surfaceError);
  }
  clubRemoveMemberImages(clubID: string, imageType: string) {
    let f = this.fbFunc.httpsCallable("clubRemoveMemberImages");
    f({
      clubID: clubID,
      imageType: imageType,
    }).catch(this.surfaceError);
  }
  profRemoveClub(clubID: string, uid: string) {
    let f = this.fbFunc.httpsCallable("profRemoveClub");
    f({
      clubID: clubID,
      uid: uid,
    }).catch(this.surfaceError);
  }
  deleteClub(clubID: string) {
    let f = this.fbFunc.httpsCallable("deleteClub");
    return f({
      clubID: clubID,
    }).catch(this.surfaceError);
  }
  recalculateLeagueStandings(leagueID: string) {
    let f = this.fbFunc.httpsCallable("recalculateLeagueStandings");
    f({
      leagueID: leagueID,
    }).catch(this.surfaceError);
  }
  calcTournPoints(tournID: string, uid: string) {
    let f = this.fbFunc.httpsCallable("calcTournPoints");
    f({
      tournID: tournID,
      uid: uid,
    }).catch(this.surfaceError);
  }
  eliminatePlayer(tournID: string, playerID: string, killerID?: string) {
    let f = this.fbFunc.httpsCallable("eliminatePlayer");
    return f({
      tournID: tournID,
      playerID: playerID,
      killerID: killerID || null,
    });
  }
  // Draw the mystery bounty for a knockout (server-authoritative). playerID is
  // the eliminated player whose bounty is recovered; killerID wins the prize.
  drawMysteryBounty(tournID: string, playerID: string, killerID: string) {
    let f = this.fbFunc.httpsCallable("drawMysteryBounty");
    return f({
      tournID: tournID,
      playerID: playerID,
      killerID: killerID,
    });
  }
  // Draw multiple pending bounties in one call. pairs: {playerID, killerID}[].
  drawMysteryBountyBatch(
    tournID: string,
    pairs: { playerID: string; killerID: string }[]
  ) {
    let f = this.fbFunc.httpsCallable("drawMysteryBountyBatch");
    return f({ tournID, pairs });
  }
  mergeTournaments(
    name: string,
    tournamentIds: string[],
    timestamp?: number
  ): Promise<{ success: boolean; tournID: string; playerCount: number; survivingPlayers: number }> {
    let f = this.fbFunc.httpsCallable("mergeTournaments");
    return f({
      name: name,
      tournamentIds: tournamentIds,
      timestamp: timestamp,
    }).then((result) => result.data);
  }
  splitTournament(
    tournID: string,
    maxPlayers: number,
    clockMode: string = "synchronized"
  ): Promise<{ success: boolean; childTournIDs: string[]; sizes: number[] }> {
    let f = this.fbFunc.httpsCallable("splitTournament");
    return f({
      tournID: tournID,
      maxPlayers: maxPlayers,
      clockMode: clockMode,
    }).then((result) => result.data);
  }
  deleteLeague(leagueID: string) {
    let f = this.fbFunc.httpsCallable("deleteLeague");
    return f({
      leagueID: leagueID,
    }).catch(this.surfaceError);
  }
  updateLeagueTournNames(leagueID: string, leagueName: string, clubID: string) {
    let f = this.fbFunc.httpsCallable("updateLeagueTournNames");
    f({
      leagueID: leagueID,
      leagueName: leagueName,
      clubID: clubID,
    }).catch(this.surfaceError);
  }
  // denied: staff denial (notifies the requester); omitted for a self-cancel.
  deleteRequest(clubID: string, uid: string, denied?: boolean) {
    let f = this.fbFunc.httpsCallable("deleteRequest");
    return f({
      clubID: clubID,
      uid: uid,
      denied: !!denied,
    }).catch(this.surfaceError);
  }
  // Club link / 4-char code: joins instantly when the club is open, else
  // files a join request. Resolves to a result code string.
  requestJoinClub(clubID: string, via: "link" | "code") {
    let f = this.fbFunc.httpsCallable("requestJoinClub");
    return f({ clubID, via }).catch(this.surfaceError);
  }
  joinClub(clubID: string, profile: any, inviteID?: string) {
    let f = this.fbFunc.httpsCallable("joinClub");
    let req: any = {
      clubID: clubID,
      profile: profile,
    };
    if (inviteID) req.inviteID = inviteID;
    return f(req).catch(this.surfaceError);
  }
  appLog(where: string, when: any, fName: string, error: string, params: any) {
    let f = this.fbFunc.httpsCallable("appLog");
    f({
      where: where,
      when: when,
      fName: fName,
      error: error,
      params: params,
      // Telemetry write — never toast the user about a failed log.
    }).catch((err) => console.error(err.code, err.message));
  }
  geoLoc() {
    let f = this.fbFunc.httpsCallable("geoLoc");
    return f().catch(this.surfaceError);
  }
  refreshSubscription(uid: string, subID: string) {
    let f = this.fbFunc.httpsCallable("refreshSubscription");
    return f({ uid, subID }).catch(this.surfaceError);
  }
  setStripeCards(uid: string, stripeID: string) {
    let f = this.fbFunc.httpsCallable("setStripeCards");
    let req: any = {
      uid: uid,
      stripeID: stripeID,
    };

    f(req).catch(this.surfaceError);
  }
  addCard(uid: string, stripeID: string, pm: string) {
    let f = this.fbFunc.httpsCallable("addCard");
    let req: any = {
      uid: uid,
      stripeID: stripeID,
      pm: pm,
    };

    return f(req).catch(this.surfaceError);
  }
  deleteCard(uid: string, stripeID: string, cardID: string) {
    let f = this.fbFunc.httpsCallable("deleteCard");
    let req: any = {
      uid: uid,
      stripeID: stripeID,
      cardID: cardID,
    };

    return f(req).catch(this.surfaceError);
  }
  changeDefaultCard(uid: string, stripeID: string, cardID: string) {
    let f = this.fbFunc.httpsCallable("changeDefaultCard");
    let req: any = {
      uid: uid,
      stripeID: stripeID,
      cardID: cardID,
    };

    return f(req).catch(this.surfaceError);
  }
  reactivateSubscription(
    uid: string,
    subID: string,
    planID: string,
    code: string,
    subItemId?: string
  ) {
    let f = this.fbFunc.httpsCallable("reactivateSubscription");
    let req: any = {
      uid: uid,
      subID: subID,
      planID: planID,
      code: code,
      subItemId: subItemId,
    };

    return f(req).catch(this.surfaceError);
  }
  cancelSubscription(uid: string, subID: string, code: string) {
    let f = this.fbFunc.httpsCallable("cancelSubscription");
    let req: any = {
      uid: uid,
      subID: subID,
      code: code,
    };

    return f(req).catch(this.surfaceError);
  }

  // Self-service account deletion. The server keys off the caller's auth uid, so
  // no uid is passed; the call must not pass an arbitrary identifier.
  deleteOwnProfile() {
    let f = this.fbFunc.httpsCallable("deleteOwnProfile");
    return f();
  }
  prorate(profile: any, change: any) {
    let f = this.fbFunc.httpsCallable("prorate");
    let req: any = {
      profile: profile,
      change: change,
    };

    return f(req).catch(this.surfaceError);
  }
  changeSubscription(profile: any, change: any) {
    let f = this.fbFunc.httpsCallable("changeSubscription");
    let req: any = {
      profile: profile,
      change: change,
    };

    return f(req).catch(this.surfaceError);
  }
  previewSubscriptionConversion(tier: string, interval: string) {
    let f = this.fbFunc.httpsCallable("previewSubscriptionConversion");
    return f({ tier, interval }).catch(this.surfaceError);
  }
  convertSubscriptionCurrency(
    tier: string,
    interval: string,
    level: number,
    player_limit: number
  ) {
    let f = this.fbFunc.httpsCallable("convertSubscriptionCurrency");
    return f({ tier, interval, level, player_limit }).catch(this.surfaceError);
  }
  payInvoice(uid: string, stripeID: string, pm: string) {
    let f = this.fbFunc.httpsCallable("payInvoice");
    let req: any = {
      uid: uid,
      stripeID: stripeID,
      pm: pm,
    };

    return f(req).catch(this.surfaceError);
  }
  mergeMember(sourceID: string, targetID: string, clubID: string) {
    let f = this.fbFunc.httpsCallable("mergeMember");
    let req: any = {
      sourceID: sourceID,
      targetID: targetID,
      clubID: clubID,
    };
    return f(req).catch(this.surfaceError);
  }
  createContactSIB(data: any) {
    let f = this.fbFunc.httpsCallable("createContactSIB");
    return f(data).catch(this.surfaceError);
  }
  setPreference(data: any) {
    let f = this.fbFunc.httpsCallable("setPreference");
    return f(data).catch(this.surfaceError);
  }
  sendEvent(messageId: string, profile: any) {
    if (profile && profile.locationData) {
      let f = this.fbFunc.httpsCallable("sendEvent");
      let data: any = {
        messageId: messageId,
        city: profile.locationData.city,
        country: profile.locationData.country,
      };
      if (profile.locationData.state) data.state = profile.locationData.state;
      if (profile.locationData.location)
        data.location = profile.locationData.location;
      // Marketing event — failure is invisible to the user on purpose.
      return f(data).catch((error) => console.error(error.code, error.message));
    }
  }
  /** Storage the signed-in account owns (clubs it owns, its websites, its
   *  profile image): { bytes, files, clubs, sites }. */
  getAccountStorage(): Promise<{ bytes: number; files: number } | null> {
    const f = this.fbFunc.httpsCallable("getAccountStorage");
    return f({})
      .then((res: any) => res.data ?? res)
      .then((r: any) => (r && typeof r.bytes === "number" ? r : null))
      .catch(() => null);
  }
  quitClub(clubID: string) {
    let f = this.fbFunc.httpsCallable("quitClub");
    return f({
      clubID: clubID,
    }).catch(this.surfaceError);
  }
}
